import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Paper,
    Typography,
    Avatar,
    CircularProgress,
    Stack,
    Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import localStrings from '../localStrings';

/**
 * Filtre le contenu JSON Langchain et les tool calls des réponses
 */
function filterLangchainJson(text) {
    if (!text) return '';

    let filtered = text;

    // Supprimer les tool calls JSON arrays [{"name":"...", "type":"tool_call"...}]
    filtered = filtered.replace(/\[\s*\{[^[\]]*"type"\s*:\s*"tool_call"[^[\]]*\}\s*\]/g, '');

    // Supprimer les blocs JSON Langchain { "lc": 1, ... }
    const jsonPattern = /\{\s*"lc"\s*:\s*1[\s\S]*?"additional_kwargs"\s*:\s*\{[^}]*\}[\s\S]*?\}\s*\}\s*\}/g;
    filtered = filtered.replace(jsonPattern, '');

    // Pattern alternatif pour les JSON qui se terminent différemment
    filtered = filtered.replace(/\{\s*"lc"\s*:\s*1,\s*"type"\s*:\s*"constructor"[\s\S]*?\}\s*\}\s*\}\s*\}/g, '');

    // Supprimer les blocs avec thoughtSignature
    filtered = filtered.replace(/\{[^{}]*"thoughtSignature"[^{}]*\}/g, '');

    // Supprimer les tool calls individuels
    filtered = filtered.replace(/\{"name"\s*:\s*"[^"]+"\s*,\s*"args"\s*:\s*\{[^}]*\}[^}]*"type"\s*:\s*"tool_call"[^}]*\}/g, '');

    // Approche plus agressive: supprimer tout ce qui ressemble à du JSON Langchain ou tool calls
    const lines = filtered.split('\n');
    const cleanLines = lines.filter(line => {
        const trimmed = line.trim();
        return !trimmed.startsWith('{"lc":') &&
               !trimmed.startsWith('{ "lc":') &&
               !trimmed.startsWith('[{"name":') &&
               !trimmed.includes('"type":"constructor"') &&
               !trimmed.includes('"type": "constructor"') &&
               !trimmed.includes('"type":"tool_call"') &&
               !trimmed.includes('"type": "tool_call"');
    });
    filtered = cleanLines.join('\n');

    // Nettoyer les espaces multiples et lignes vides
    filtered = filtered.replace(/\n{3,}/g, '\n\n').trim();

    return filtered;
}

/**
 * Vérifie si un texte est un tool call ou du JSON technique à ignorer
 */
function isToolCallOrTechnical(text) {
    if (!text) return false;
    const trimmed = text.trim();

    // Tool calls
    if (trimmed.startsWith('[{"name":') && trimmed.includes('"tool_call"')) return true;
    if (trimmed.includes('"type":"tool_call"') || trimmed.includes('"type": "tool_call"')) return true;

    // Langchain JSON
    if (trimmed.startsWith('{"lc":') || trimmed.startsWith('{ "lc":')) return true;

    return false;
}

/**
 * Composant de message individuel avec support Markdown
 */
function ChatMessage({ message, isUser, userAvatar, isStreaming }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 2,
                px: 1
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    maxWidth: '80%',
                    gap: 1.5
                }}
            >
                <Avatar
                    src={isUser ? userAvatar : undefined}
                    sx={{
                        bgcolor: isUser ? 'primary.main' : '#3B81F6',
                        width: 40,
                        height: 40
                    }}
                >
                    {isUser ? <PersonIcon /> : <SmartToyIcon />}
                </Avatar>
                <Paper
                    elevation={1}
                    sx={{
                        p: 1.5,
                        bgcolor: isUser ? 'primary.main' : 'grey.100',
                        color: isUser ? 'white' : 'text.primary',
                        borderRadius: 2,
                        // Styles Markdown
                        '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                        '& a': {
                            color: isUser ? 'white' : 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': { opacity: 0.8 }
                        },
                        '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                        '& li': { mb: 0.5 },
                        '& code': {
                            bgcolor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.85em'
                        },
                        '& pre': {
                            bgcolor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                            p: 1,
                            borderRadius: 1,
                            overflow: 'auto',
                            '& code': { bgcolor: 'transparent', p: 0 }
                        },
                        '& strong': { fontWeight: 'bold' },
                        '& em': { fontStyle: 'italic' },
                        '& h1, & h2, & h3, & h4': { mt: 1, mb: 0.5, fontWeight: 'bold' },
                        '& blockquote': {
                            borderLeft: '3px solid',
                            borderColor: isUser ? 'rgba(255,255,255,0.5)' : 'grey.400',
                            pl: 1,
                            ml: 0,
                            fontStyle: 'italic'
                        },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1
                        }
                    }}
                >
                    {isUser ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message}
                        </Typography>
                    ) : (
                        <Box>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ href, children }) => (
                                        <a href={href} target="_blank" rel="noopener noreferrer">
                                            {children}
                                        </a>
                                    )
                                }}
                            >
                                {message}
                            </ReactMarkdown>
                            {isStreaming && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 16,
                                        bgcolor: 'primary.main',
                                        ml: 0.5,
                                        animation: 'blink 1s infinite',
                                        '@keyframes blink': {
                                            '0%, 50%': { opacity: 1 },
                                            '51%, 100%': { opacity: 0 }
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
}

/**
 * Composant FlowiseChat - Chat personnalisé avec l'API Flowise
 * Supporte le streaming SSE, bouton stop et microphone
 */
function FlowiseChat({
    chatflowId,
    apiHost,
    sessionId,
    welcomeMessage,
    placeholder,
    title,
    titleBackgroundColor = '#3B81F6',
    titleTextColor = '#ffffff',
    vars = {},
    onReset,
    userAvatar
}) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [messageHistory, setMessageHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isListening, setIsListening] = useState(false);
    const [speechSupported, setSpeechSupported] = useState(false);
    const [sessionSuffix, setSessionSuffix] = useState(() => Date.now());

    // Session ID unique combinant le sessionId prop et un suffix pour les nouvelles conversations
    const currentSessionId = `${sessionId}-${sessionSuffix}`;

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const abortControllerRef = useRef(null);
    const recognitionRef = useRef(null);

    // Vérifier le support de la reconnaissance vocale
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setSpeechSupported(true);
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'fr-FR';

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                if (finalTranscript) {
                    setInput(prev => prev + finalTranscript);
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    // Ajouter le message de bienvenue au chargement
    useEffect(() => {
        if (welcomeMessage && messages.length === 0) {
            setMessages([{ text: welcomeMessage, isUser: false }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [welcomeMessage]);

    // Reset de la session
    const handleReset = useCallback(() => {
        // Annuler toute requête en cours
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // Générer un nouveau suffix de session pour démarrer une nouvelle conversation
        setSessionSuffix(Date.now());
        setMessages(welcomeMessage ? [{ text: welcomeMessage, isUser: false }] : []);
        setMessageHistory([]);
        setHistoryIndex(-1);
        setInput('');
        setIsLoading(false);
        setIsStreaming(false);
        if (onReset) onReset();
    }, [welcomeMessage, onReset]);

    // Scroll vers le bas quand de nouveaux messages arrivent
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Toggle microphone
    const toggleListening = () => {
        if (!speechSupported || !recognitionRef.current) return;

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            recognitionRef.current.start();
            setIsListening(true);
        }
    };

    // Arrêter le streaming
    const stopStreaming = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsStreaming(false);
        setIsLoading(false);
    };

    // Envoyer un message avec streaming SSE
    const sendMessage = async () => {
        const userMessage = input.trim();
        if (!userMessage || isLoading) return;

        // Arrêter le microphone si actif
        if (isListening && recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListening(false);
        }

        // Ajouter à l'historique des messages utilisateur
        setMessageHistory(prev => [...prev, userMessage]);
        setHistoryIndex(-1);

        // Ajouter le message utilisateur
        setMessages(prev => [...prev, { text: userMessage, isUser: true }]);
        setInput('');
        setIsLoading(true);

        // Créer un AbortController pour pouvoir annuler la requête
        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch(`${apiHost}/api/v1/prediction/${chatflowId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    question: userMessage,
                    chatId: currentSessionId,
                    streaming: true,
                    overrideConfig: {
                        sessionId: currentSessionId,
                        vars: vars
                    }
                }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            console.log('Response content-type:', contentType);
            console.log('Response status:', response.status);

            // Vérifier si c'est du streaming SSE
            if (contentType && contentType.includes('text/event-stream')) {
                console.log('MODE: Streaming SSE');
                setIsStreaming(true);

                // Ajouter un message vide pour le bot
                setMessages(prev => [...prev, { text: '', isUser: false, isStreaming: true }]);

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let botResponse = '';
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        console.log('Stream done. Final response:', botResponse);
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });
                    console.log('Raw chunk received:', chunk);
                    buffer += chunk;
                    const lines = buffer.split('\n');

                    // Garder la dernière ligne incomplète dans le buffer
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) continue;

                        console.log('Processing line:', trimmedLine);

                        if (trimmedLine.startsWith('data:')) {
                            const data = trimmedLine.slice(5).trim();
                            console.log('Data extracted:', data);
                            if (data && data !== '[DONE]') {
                                try {
                                    const parsed = JSON.parse(data);
                                    console.log('Parsed JSON:', parsed);

                                    // Flowise AgentFlow envoie différents types d'événements
                                    const eventType = parsed.event;

                                    // Ignorer les événements de statut
                                    if (eventType === 'agentFlowEvent' ||
                                        eventType === 'nextAgentFlow' ||
                                        eventType === 'start' ||
                                        eventType === 'end') {
                                        console.log('Skipping status event:', eventType);
                                        continue;
                                    }

                                    // Événement token = streaming de texte
                                    if (eventType === 'token' && parsed.data) {
                                        const token = typeof parsed.data === 'string' ? parsed.data : '';
                                        if (token) {
                                            botResponse += token;
                                            console.log('Token added:', token);
                                            setMessages(prev => {
                                                const newMessages = [...prev];
                                                const lastIndex = newMessages.length - 1;
                                                if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                                                    newMessages[lastIndex] = {
                                                        ...newMessages[lastIndex],
                                                        text: filterLangchainJson(botResponse)
                                                    };
                                                }
                                                return newMessages;
                                            });
                                        }
                                        continue;
                                    }

                                    // Événement agentFlowExecutedData = données d'exécution avec output potentiel
                                    if (eventType === 'agentFlowExecutedData' && Array.isArray(parsed.data)) {
                                        for (const node of parsed.data) {
                                            // Chercher la sortie finale de l'agent
                                            const output = node?.data?.output;
                                            if (output) {
                                                // La réponse peut être dans différentes clés
                                                const text = output.text || output.output || output.response ||
                                                            output.answer || output.content || output.message;
                                                if (text && typeof text === 'string' && text.length > 10) {
                                                    console.log('Found output text:', text);
                                                    botResponse = text; // Remplacer, pas ajouter
                                                    setMessages(prev => {
                                                        const newMessages = [...prev];
                                                        const lastIndex = newMessages.length - 1;
                                                        if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                                                            newMessages[lastIndex] = {
                                                                ...newMessages[lastIndex],
                                                                text: filterLangchainJson(botResponse)
                                                            };
                                                        }
                                                        return newMessages;
                                                    });
                                                }
                                            }
                                        }
                                        continue;
                                    }

                                    // Fallback: chercher token/text directement
                                    const token = parsed.token || parsed.text || parsed.message ||
                                                  (typeof parsed.data === 'string' ? parsed.data : '');
                                    if (token && token.length > 0) {
                                        // Ignorer les tool calls et JSON technique
                                        if (isToolCallOrTechnical(token)) {
                                            console.log('Skipping tool call/technical:', token.substring(0, 50) + '...');
                                            continue;
                                        }
                                        botResponse += token;
                                        console.log('Fallback token added:', token);
                                        setMessages(prev => {
                                            const newMessages = [...prev];
                                            const lastIndex = newMessages.length - 1;
                                            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                                                newMessages[lastIndex] = {
                                                    ...newMessages[lastIndex],
                                                    text: filterLangchainJson(botResponse)
                                                };
                                            }
                                            return newMessages;
                                        });
                                    }
                                } catch (e) {
                                    console.log('JSON parse failed, trying as raw text:', data);
                                    // Si ce n'est pas du JSON, c'est peut-être du texte brut
                                    if (data && !data.startsWith('{') && !data.startsWith('[')) {
                                        botResponse += data;
                                        setMessages(prev => {
                                            const newMessages = [...prev];
                                            const lastIndex = newMessages.length - 1;
                                            if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                                                newMessages[lastIndex] = {
                                                    ...newMessages[lastIndex],
                                                    text: filterLangchainJson(botResponse)
                                                };
                                            }
                                            return newMessages;
                                        });
                                    }
                                }
                            }
                        } else if (trimmedLine.startsWith('event:')) {
                            console.log('Event line:', trimmedLine);
                            continue;
                        } else {
                            // Peut-être du texte brut sans préfixe "data:"
                            console.log('Unknown line format:', trimmedLine);
                        }
                    }
                }

                // Finaliser le message
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastIndex = newMessages.length - 1;
                    if (lastIndex >= 0 && !newMessages[lastIndex].isUser) {
                        newMessages[lastIndex] = {
                            ...newMessages[lastIndex],
                            text: filterLangchainJson(botResponse) || localStrings.chatAiNoResponse,
                            isStreaming: false
                        };
                    }
                    return newMessages;
                });

            } else {
                // Réponse JSON classique (pas de streaming)
                console.log('MODE: JSON classique');
                const data = await response.json();
                console.log('Flowise response:', JSON.stringify(data, null, 2));

                // Flowise peut retourner différents formats
                let botResponse = '';

                // Fonction récursive pour extraire le texte de la réponse
                const extractText = (obj) => {
                    if (!obj) return '';
                    if (typeof obj === 'string') return obj;

                    // Priorité aux clés connues
                    const priorityKeys = ['text', 'output', 'response', 'answer', 'message', 'content', 'result'];
                    for (const key of priorityKeys) {
                        if (obj[key]) {
                            if (typeof obj[key] === 'string') return obj[key];
                            if (typeof obj[key] === 'object') return extractText(obj[key]);
                        }
                    }

                    // Chercher dans les sous-objets
                    for (const key of Object.keys(obj)) {
                        const value = obj[key];
                        if (typeof value === 'string' && value.length > 20) {
                            // Probablement le contenu de la réponse
                            return value;
                        }
                        if (typeof value === 'object' && !Array.isArray(value)) {
                            const nested = extractText(value);
                            if (nested) return nested;
                        }
                    }
                    return '';
                };

                botResponse = extractText(data);
                console.log('Extracted response:', botResponse);

                botResponse = filterLangchainJson(botResponse);

                if (!botResponse.trim()) {
                    // Si toujours vide, afficher l'objet brut pour debug
                    console.error('Could not extract response from:', data);
                    botResponse = localStrings.chatAiNoResponse;
                }

                setMessages(prev => [...prev, { text: botResponse, isUser: false }]);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // Requête annulée par l'utilisateur
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastIndex = newMessages.length - 1;
                    if (lastIndex >= 0 && !newMessages[lastIndex].isUser && newMessages[lastIndex].isStreaming) {
                        newMessages[lastIndex] = {
                            ...newMessages[lastIndex],
                            text: newMessages[lastIndex].text + '\n\n*[Réponse interrompue]*',
                            isStreaming: false
                        };
                    }
                    return newMessages;
                });
            } else {
                console.error('Erreur Flowise:', error);
                setMessages(prev => [...prev, {
                    text: localStrings.chatAiErrorMessage,
                    isUser: false
                }]);
            }
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (e) => {
        // Envoyer avec Enter (sans Shift)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
            return;
        }

        // Navigation dans l'historique avec flèche haut/bas
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (messageHistory.length === 0) return;

            const newIndex = historyIndex === -1
                ? messageHistory.length - 1
                : Math.max(0, historyIndex - 1);

            setHistoryIndex(newIndex);
            setInput(messageHistory[newIndex]);
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex === -1) return;

            const newIndex = historyIndex + 1;
            if (newIndex >= messageHistory.length) {
                setHistoryIndex(-1);
                setInput('');
            } else {
                setHistoryIndex(newIndex);
                setInput(messageHistory[newIndex]);
            }
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: 'background.paper'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: titleBackgroundColor,
                    color: titleTextColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                }}
            >
                <Avatar
                    src="https://raw.githubusercontent.com/walkxcode/dashboard-icons/main/svg/google-messages.svg"
                    sx={{ width: 36, height: 36 }}
                />
                <Typography variant="h6" fontWeight="medium" sx={{ flex: 1 }}>
                    {title}
                </Typography>
                <Tooltip title={localStrings.chatAiNewConversation}>
                    <IconButton
                        onClick={handleReset}
                        sx={{
                            color: titleTextColor,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Messages */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    py: 2,
                    bgcolor: 'grey.50'
                }}
            >
                {messages.map((msg, index) => (
                    <ChatMessage
                        key={index}
                        message={msg.text}
                        isUser={msg.isUser}
                        userAvatar={userAvatar}
                        isStreaming={msg.isStreaming}
                    />
                ))}

                {isLoading && !isStreaming && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#3B81F6', width: 40, height: 40 }}>
                                <SmartToyIcon />
                            </Avatar>
                            <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">
                                        {localStrings.chatAiThinking}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Box>
                    </Box>
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box
                sx={{
                    p: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Bouton Microphone */}
                    {speechSupported && (
                        <Tooltip title={isListening ? localStrings.chatAiStopListening : localStrings.chatAiStartListening}>
                            <IconButton
                                onClick={toggleListening}
                                disabled={isLoading}
                                sx={{
                                    bgcolor: isListening ? 'error.main' : 'grey.200',
                                    color: isListening ? 'white' : 'grey.700',
                                    width: 40,
                                    height: 40,
                                    '&:hover': {
                                        bgcolor: isListening ? 'error.dark' : 'grey.300'
                                    },
                                    animation: isListening ? 'pulse 1.5s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
                                        '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                                        '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                                    }
                                }}
                            >
                                {isListening ? <MicOffIcon /> : <MicIcon />}
                            </IconButton>
                        </Tooltip>
                    )}

                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={4}
                        autoComplete="off"
                        placeholder={isListening ? localStrings.chatAiListening : placeholder}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading && !isStreaming}
                        InputProps={{
                            sx: {
                                borderRadius: 6,
                                minHeight: 48,
                                fontSize: '1.1rem',
                                bgcolor: isListening ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                                alignItems: 'center',
                                py: 1,
                                '& textarea': {
                                    fontSize: '1.1rem'
                                },
                                '& textarea::placeholder': {
                                    fontSize: '1.1rem',
                                    opacity: 0.7
                                }
                            }
                        }}
                    />

                    {/* Bouton Stop ou Envoyer */}
                    {isStreaming ? (
                        <Tooltip title={localStrings.chatAiStopResponse}>
                            <IconButton
                                onClick={stopStreaming}
                                sx={{
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    '&:hover': { bgcolor: 'error.dark' }
                                }}
                            >
                                <StopIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <IconButton
                            color="primary"
                            onClick={sendMessage}
                            disabled={!input.trim() || isLoading}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'primary.dark' },
                                '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default FlowiseChat;
